import { Dropdown } from 'primereact/dropdown';
import { FC, Ref, useState, useMemo, ReactElement } from 'react';

import countries, { CountryType } from '@/lib/countries';

export type PhoneInputProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  inputRef?: Ref<HTMLInputElement>;
  selectClassName?: string;
  inputClassName?: string;
  disabled?: boolean;
};

const getDefaultCountry = (): CountryType =>
  countries.find((c) => c.code === 'ZA') || countries[0];

const normalizePhonePrefix = (phone: string): string =>
  phone.replace(/\D/g, '');

const findCountryByPhonePrefix = (value: string): CountryType | null => {
  if (!value) return null;
  const digits = value.replace(/\D/g, '');

  return (
    countries
      .slice()
      .sort(
        (a, b) =>
          normalizePhonePrefix(b.phone).length -
          normalizePhonePrefix(a.phone).length
      )
      .find((c) => digits.startsWith(normalizePhonePrefix(c.phone))) || null
  );
};

const ensureE164 = (countryPhone: string, local: string): string => {
  const cleanLocal = (local || '').replace(/\D/g, '');
  const cleanCountryPhone = normalizePhonePrefix(countryPhone);
  return `+${cleanCountryPhone}${cleanLocal}`;
};

const stripCountryFromE164 = (countryPhone: string, full: string): string => {
  const digits = (full || '').replace(/\D/g, '');
  const cleanCountryPhone = normalizePhonePrefix(countryPhone);

  return digits.startsWith(cleanCountryPhone)
    ? digits.slice(cleanCountryPhone.length)
    : digits;
};

const PhoneInput: FC<PhoneInputProps> = ({
  id,
  value,
  onChange,
  onBlur,
  inputRef,
  selectClassName = '',
  inputClassName = '',
  disabled = false,
}) => {
  const [selectedCode, setSelectedCode] = useState<string>(
    getDefaultCountry().code
  );

  const countryOptions = useMemo(
    () =>
      countries.map((c) => ({
        label: `+${c.phone} ${c.label}`,
        value: c.code,
        code: c.code,
        phone: c.phone,
        name: c.label,
      })),
    []
  );

  const toFlagEmoji = (code: string): string =>
    code
      .toUpperCase()
      .replace(/./g, (char) =>
        String.fromCodePoint(127397 + char.charCodeAt(0))
      );

  const renderCountryOption = (
    code: string,
    label: string
  ): ReactElement<any, any> => {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xl" aria-hidden>
          {toFlagEmoji(code)}
        </span>
        <span className="text-sm text-zinc-800">{label}</span>
      </div>
    );
  };

  const renderValueTemplate = (
    code: string,
    currentCountryPhone: string
  ): ReactElement<any, any> => {
    const phone =
      countries.find((c) => c.code === code)?.phone || currentCountryPhone;

    return (
      <div className="flex items-center gap-2">
        <span className="text-xl" aria-hidden>
          {toFlagEmoji(code)}
        </span>
        <span className="text-sm text-zinc-800">+{phone}</span>
      </div>
    );
  };

  const preferredCountry =
    countries.find((c) => c.code === selectedCode) || getDefaultCountry();

  const detectedCountry = findCountryByPhonePrefix(value);
  const currentCountry =
    preferredCountry || detectedCountry || getDefaultCountry();

  const localPart = stripCountryFromE164(currentCountry.phone, value || '');

  return (
    <div className="flex gap-2.5 items-center">
      <Dropdown
        value={currentCountry.code}
        options={countryOptions}
        optionLabel="label"
        filter
        filterBy="phone,name"
        filterPlaceholder="Search"
        pt={{
          header: {
            className: 'mb-2 border-b border-zinc-200',
          },
        }}
        focusOnHover={false}
        itemTemplate={(option) =>
          renderCountryOption(option.code, option.label)
        }
        valueTemplate={(selected) => {
          const code =
            (selected && (selected.code || selected.value)) ||
            currentCountry.code;
          return renderValueTemplate(code, currentCountry.phone);
        }}
        className={selectClassName}
        disabled={disabled}
        appendTo={document.body}
        panelClassName="bg-white shadow-xl ring-1 ring-black/5 z-50 p-2 border border-zinc-300 rounded-md"
        onChange={(e) => {
          const code = e.value as string;
          const newCountry =
            countries.find((c) => c.code === code) || getDefaultCountry();
          setSelectedCode(code);
          const next = ensureE164(newCountry.phone, localPart);
          onChange(next);
        }}
      />

      <input
        id={id}
        ref={inputRef}
        type="tel"
        className={inputClassName}
        inputMode="numeric"
        value={localPart}
        onBlur={onBlur}
        disabled={disabled}
        onChange={(e) => {
          const text = e.target.value.replace(/\D/g, '');
          const next = ensureE164(currentCountry.phone, text);
          onChange(next);
        }}
      />
    </div>
  );
};

export default PhoneInput;
